import { format } from "date-fns";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaCalendar, FaClock } from "react-icons/fa";
import Select, {
  components,
  CSSObjectWithLabel,
  GroupBase,
  OptionsOrGroups,
} from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import AsyncSelect from "react-select/async";
import { DatePicker } from "rsuite";
import { TypeAttributes } from "rsuite/esm/internals/types";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../constants/constants";
import { formattedDateTime } from "../../utils/dateTime";
import TriStateCheckbox from "../common/TriStateCheckbox";
import ErrorBoundaryForSection from "../errorBoundaryForSection";

import "react-datepicker/dist/react-datepicker.css";
import "rsuite/DatePicker/styles/index.css";
import "rsuite/DateRangePicker/styles/index.css";
import { useThemeAssets } from "../common/ThemeContext";

// exports
export { AdminFormFieldDurationInput } from "./formFieldDurationInput";

type AdminFormFieldInputProps = {
  label: string;
  subText?: string;
  bottomLabel?: string;
  labelSubtext?: string;
  type: string;
  id: string;
  name: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDatepickerChange?: (fieldName: string, value: any) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value?: any;
  touched?: boolean;
  error?: string;
  showErrorValue?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  detailsFormField?: boolean;
  basicInputClass?: string;
  customInputClass?: string;
  innerWrapperClass?: string;
  customWrapperClass?: string;
  disabledClass?: string;
  // datepicker
  calendarIconDimensionClass?: string;
  enableSecondsSelect?: boolean;
  maxTime?: Date;
  minTime?: Date;
  showProcessingIndicator?: boolean;
  size?: string;
  AsideFieldButtons?: ReactNode;
  highlightedDates?: string[]; // Array of dates in "yyyy-MM-dd" format to show additional blue dot below date in calendar
  placement?: TypeAttributes.Placement | undefined;
  renderCalendarPopupInBody?: boolean;
};

const ReactDatepickerCustomTimeInput = ({ date, onChangeCustom }: any) => {
  const value =
    date instanceof Date && !isNaN(date as any)
      ? // Getting time from Date beacuse `value` comes here without seconds
      date.toLocaleTimeString("it-IT")
      : "";

  return (
    <input
      type="time"
      step="1"
      value={value}
      onChange={(event) => onChangeCustom(date, event.target.value)}
    />
  );
};

export const AdminFormFieldInput = React.forwardRef<
  HTMLInputElement,
  AdminFormFieldInputProps
>(
  (
    {
      label,
      bottomLabel,
      labelSubtext,
      type,
      id,
      name,
      placeholder = "",
      onChange,
      onDatepickerChange,
      onBlur,
      value,
      touched,
      error,
      disabled = false,
      readOnly = false,
      showErrorValue = true,
      detailsFormField = false,
      basicInputClass = "w-full h-11 rounded-md text-sm",
      customInputClass = "",
      innerWrapperClass = "mt-1 relative flex w-full",
      customWrapperClass = "col-span-6",
      // datepicker
      calendarIconDimensionClass = "right-2 top-1/2 -translate-y-1/2 size-6",
      enableSecondsSelect = false,
      maxTime,
      minTime,
      showProcessingIndicator = false,
      size,
      AsideFieldButtons = null,
      highlightedDates = [],
      placement,
      renderCalendarPopupInBody = false,
    },
    ref
  ) => {
    const fieldHasError = detailsFormField
      ? !disabled && !!error
      : touched && !!error;
    let wrapperClass = fieldHasError ? "" : "";
    let labelClass = fieldHasError
      ? "text-field-error-dark"
      : "text-field-label-valid";
    let inputClass = fieldHasError
      ? "border-field-error-border focus-visible:outline-field-error-dark text-field-error-dark"
      : "border-gray-200 text-field-label-valid";
    const floatingError = false;

    const [showPassword, setShowPassword] = useState(false);
    const [passwordFieldType, setPasswordFieldType] = useState(type);
    const divRef = useRef<HTMLDivElement>(null);
    const [defaultDate, setDefaultDate] = useState<Date | null>(null);
    const togglePassword = () => {
      setPasswordFieldType(showPassword ? "password" : "text");
      setShowPassword(!showPassword);
    };
    useEffect(() => {
      if (
        value &&
        typeof value === "string" &&
        value.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        setDefaultDate(new Date(`${value}T00:00:00`));
      } else {
        setDefaultDate(value ? new Date(value) : null);
      }
    }, [value]);
    const handleDatetimePickerChange = (value: Date | null) => {
      if (!value) {
        setDefaultDate(null);
        return;
      }

      if (type === "time-picker") {
        // Extract time from Date object
        const time = value.toLocaleTimeString("it-IT");
        console.log(time, "time");
        handleChangeTime(time);
      } else {
        const outputFormat = enableSecondsSelect
          ? APP_CONFIG.DATETIME_FORMATS.DATEPICKER.OUTPUT_DATE_TIME_SEC
          : APP_CONFIG.DATETIME_FORMATS.DATEPICKER.OUTPUT_DATE_TIME;

        const formattedValue = formattedDateTime(value, outputFormat);
        console.log(
          "DATE:--date picker date selected",
          formattedDateTime(
            formattedValue,
            APP_CONFIG.DATETIME_FORMATS.DATEPICKER.OUTPUT_DATE_TIME
          )
        );
        // Example value that needs to be stored - 2024-09-13T19:30
        onDatepickerChange?.(name, formattedValue);
      }
    };

    const handleChangeTime = (time: string) => {
      if (!!time) {
        onDatepickerChange?.(name, time);
      }
    };

    return (
      <div
        className={`relative ${
          floatingError ? ` pb-4` : ""
          } ${wrapperClass} ${customWrapperClass}`}
      >
        <ErrorBoundaryForSection wrapClass="w-full h-full" fontSize="compact">
          <label
            htmlFor={id}
            className={`block text-sm font-display font-semibold ${labelClass}`}
          >
            {label}
            {labelSubtext && (
              <span className="inline-block text-[10px] text-gray-400 ms-2">
                {labelSubtext}
              </span>
            )}
          </label>
          <div className={innerWrapperClass} ref={divRef}>
            {showProcessingIndicator && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="processing"
                  className="size-8"
                />
              </div>
            )}
            {["datetime-picker", "date-picker", "time-picker"].includes(
              type
            ) ? (
              <DatePicker
                ref={ref as any}
                format={
                  type === "time-picker"
                    ? "hh:mm:ss aa" // Format for time-picker
                    : type === "date-picker"
                      ? "MM/dd/yyyy" // Format for date-picker
                      : enableSecondsSelect
                        ? "MM/dd/yyyy hh:mm:ss aa"
                        : "MM/dd/yyyy hh:mm aa" // Format for datetime-picker
                }
                onChange={handleDatetimePickerChange}
                container={
                  renderCalendarPopupInBody
                    ? () => document.body
                    : () => divRef.current || document.body
                }
                value={defaultDate}
                size={size as "sm" | "md" | "lg"}
                className={`${basicInputClass} px-0 enabled:outline-accent-blue-dark focus-visible:outline-4 focus-visible:shadow-none${
                  touched ? " touched" : ""
                  } ${disabled || readOnly ? "bg-gray-100" : "bg-white"} ${inputClass} ${customInputClass}`}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={
                  type === "time-picker"
                    ? "HH:MM:SS AA"
                    : type === "date-picker"
                      ? "MM/DD/YYYY"
                      : "MM/DD/YYYY HH:MM:SS AA"
                }
                editable={false}
                {...(type !== "date-picker" && {
                  caretAs: type === "time-picker" ? FaClock : FaCalendar,
                })}
                showMeridiem={type !== "date-picker" && enableSecondsSelect}
                {...(maxTime && { max: new Date(maxTime) })}
                {...(minTime && { min: new Date(minTime) })}
                renderCell={(date: Date) => {
                  return (
                    <div className="flex flex-col justify-center items-center">
                      <h2>{format(date, "dd")}</h2>
                      {highlightedDates?.length > 0 &&
                        highlightedDates?.includes(
                          format(date, "yyyy-MM-dd")
                        ) && <div className="bg-blue-400 w-1 h-1 rounded-sm" />}
                    </div>
                  );
                }}
                placement={placement}
              />
            ) : (
              // autoComplete "new-password": browsers ignore "off" on password fields and fill in
              // the admin's own saved password, which a save would then set on the user being edited.
              <input
                ref={ref}
                {...(type === "password" && { autoComplete: "new-password" })}
                type={type === "password" ? passwordFieldType : type}
                id={id}
                name={name}
                placeholder={placeholder}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                disabled={disabled}
                readOnly={readOnly}
                className={`${basicInputClass} px-3 enabled:outline-accent-blue-dark focus-visible:outline-4 focus-visible:shadow-none${
                  touched ? " touched" : ""
                  } ${disabled || readOnly ? "bg-gray-100" : "bg-white"} ${inputClass}${type === "password" ? " tracking-widest placeholder-tracking-normal" : ""} ${customInputClass}`}
              />
            )}
            {AsideFieldButtons}
          </div>
          {type === "password" && !disabled && !readOnly && (
            <button
              type="button"
              onClick={togglePassword}
              className={`absolute p-2 right-3 mt-[2px] top-11 transform -translate-y-1/2 z-10`}
            >
              <img
                src={
                  showPassword
                    ? svgIcons.HidePasswordIcon
                    : svgIcons.ShowPasswordIcon
                }
                alt="toggle password"
                className="size-4"
              />
            </button>
          )}
          {bottomLabel && (
            <span className="block text-[10px] text-gray-400 mt-1">
              {bottomLabel}
            </span>
          )}
          {fieldHasError && showErrorValue && (
            <p
              className={`${
                floatingError ? "absolute" : ""
                } bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
            >
              {error}
            </p>
          )}
        </ErrorBoundaryForSection>
      </div>
    );
  }
);

export type TSelectboxOption = {
  value: string;
  label: string;
};

type AdminFormFieldDropdownProps = {
  loadingData?: boolean;
  label: string | false;
  id: string;
  name: string;
  HTMLtitle?: string;
  placeholder?: ReactNode | string;
  // onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onChange?: (e: TSelectboxOption | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  value?: TSelectboxOption | TSelectboxOption[] | string | null;
  options?: TSelectboxOption[];
  touched?: boolean;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  detailsFormField?: boolean;
  dimensionClass?: string;
  customSelectboxClass?: string;
  customWrapperClass?: string;
  isClearable?: boolean;
  isMulti?: boolean;

  labelSubtext?: string;
  bottomLabel?: string;

  menuPlacement?: "auto" | "top" | "bottom";
  maxMenuHeight?: number | undefined;
};

type AdminFormFieldAsyncDropdownProps = AdminFormFieldDropdownProps & {
  HideDropdownIndicator?: boolean;
  noOptionsMessage?: (obj: { inputValue: string }) => string;
  defaultOption?: TSelectboxOption;
  defaultOptions?: TSelectboxOption[];
  loadOptions:
  | ((
    inputValue: string,
    callback: (
      options: OptionsOrGroups<
        TSelectboxOption,
        GroupBase<TSelectboxOption>
      >
    ) => void
  ) => void) /* | Promise<...>*/
  | undefined;
  searchStyle?: boolean;
  searchStyleOnClear?: () => void;
  isMulti?: boolean;
};

export type TLoadOptions = (
  inputValue: string,
  prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
  additional?: { page: number }
) => Promise<{
  options: OptionsOrGroups<unknown, GroupBase<unknown>>;
  hasMore: boolean;
  additional?: { page: number };
}>;

type AdminFormFieldAsyncLazyDropdownProps = AdminFormFieldDropdownProps & {
  HideDropdownIndicator?: boolean;
  noOptionsMessage?: (obj: { inputValue: string }) => string;
  defaultOption?: TSelectboxOption;
  defaultOptions?: TSelectboxOption[];
  loadOptions: TLoadOptions;
  additional?: { page: number };
  searchStyle?: boolean;
  searchStyleOnClear?: () => void;
  isMulti?: boolean;
  isSearchable?: boolean;
};

export const PseudoSelect: FC<{
  customWrapperClass?: string;
  label: string | false;
  dimensionClass?: string;
}> = ({
  customWrapperClass = "col-span-6",
  label,
  dimensionClass = "w-full h-11",
}) => {
    return (
      <div className={customWrapperClass}>
        <label className={`block text-sm font-display font-semibold`}>
          {label}
        </label>
        <div
          className={`mt-1 ${dimensionClass} px-1 rounded-md bg-white text-sm shadow-sm border focus-visible:outline-4 focus-visible:shadow-none¯ overflow-hidden`}
        >
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-9 bg-gray-200 rounded w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

const RealSelect: FC<AdminFormFieldDropdownProps> = ({
  label,
  id,
  name,
  HTMLtitle,
  placeholder = "",
  onChange,
  onBlur,
  value,
  options = [],
  touched,
  error,
  disabled = false,
  readOnly = false,
  detailsFormField = false,
  dimensionClass = "w-full h-11",
  customSelectboxClass = "",
  customWrapperClass = "col-span-6",
  isClearable = false,
  isMulti = false,

  labelSubtext,
  bottomLabel,

  menuPlacement = "auto",
}) => {
  const { colors } = useThemeAssets();
  const fieldHasError = detailsFormField
    ? !disabled && !!error
    : touched && !!error;
  let wrapperClass = fieldHasError ? "" : "";
  let labelClass = fieldHasError
    ? "text-field-error-dark"
    : "text-field-label-valid";
  let inputClass = fieldHasError
    ? "border-field-error-border focus-visible:outline-field-error-dark text-field-error-dark"
    : "text-field-label-valid";
  const floatingError = false;

  const [selected, setSelected] = useState<TSelectboxOption[] | string | null>(
    value ?? ""
  );
  const handleChange = (e: TSelectboxOption | null) => {
    setSelected(`${e?.value}`);
    onChange?.(e);
  };

  return (
    <div
      className={`${
        floatingError ? ` relative pb-4` : ""
        } ${wrapperClass} ${customWrapperClass}`}
      title={HTMLtitle}
    >
      {!!label && (
        <label
          htmlFor={id}
          className={`block text-sm font-display font-semibold ${labelClass}`}
        >
          {label}
          {labelSubtext && (
            <span className="inline-block text-[10px] text-gray-400 ms-2">
              {labelSubtext}
            </span>
          )}
        </label>
      )}

      <Select
        inputId={id}
        classNames={{
          control: (controlProps) =>
            `mt-1 ${dimensionClass} px-1 rounded-md bg-white text-sm shadow-sm border focus-visible:outline-4 focus-visible:shadow-none ${
              controlProps.isFocused ? "border-red-600" : "border-grey-300"
            } ${
              controlProps.isDisabled ? "bg-gray-400" : ""
            } ${inputClass} ${customSelectboxClass}`,
        }}
        isClearable={isClearable}
        isMulti={isMulti}
        styles={{
          control: (styles, controlProps) => {
            return {
              ...styles,
              boxShadow: "none",
              border:
                !!error && !controlProps.isDisabled
                  ? `1px solid #D0021B`
                  : controlProps.isFocused
                    ? "1px solid #005B90"
                    : "1px solid #e5e7eb",
              outline:
                !!error && !controlProps.isDisabled
                  ? `0px solid #D0021B`
                  : controlProps.isFocused
                    ? "1px solid #005B90"
                    : "0px solid #e5e7eb",
              // borderRadius: '6px',
              // boxShadow: 'none',
              // outline: 'none',
              "&:hover": {
                outlineColor:
                  !!error && !controlProps.isDisabled
                    ? "#D0021B"
                    : controlProps.isFocused
                      ? "#005B90"
                      : "#e5e7eb",
              },
            };
          },
          menu: (styles) => {
            return {
              ...styles,
              zIndex: APP_CONFIG.DROPDOWN_ZINDEX,
            };
          },
          option: (styles, { isSelected, isFocused }) => {
            return {
              ...styles,
              ...(colors
                ? {
                  backgroundColor: isSelected
                    ? colors.dark
                    : isFocused
                      ? colors.fade
                      : "",
                }
                : {}),
            };
          },
        }}
        placeholder={placeholder}
        options={options}
        value={
          isMulti
            ? value
            : options.find((optItem) => optItem.value === selected)
        }
        onChange={(e: any) => handleChange(e)}
        isDisabled={disabled || readOnly}
        // onBlur={onBlur}
        menuPlacement={menuPlacement}
      />
      {bottomLabel && (
        <span className="block text-[10px] text-gray-400 mt-1">
          {bottomLabel}
        </span>
      )}
      {fieldHasError && (
        <p
          className={`${
            floatingError ? "absolute" : ""
            } bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export const AdminFormFieldDropdown: FC<AdminFormFieldDropdownProps> = (
  props
) => {
  return (
    <>
      {props?.loadingData ? (
        <PseudoSelect label={props.label} />
      ) : (
        <RealSelect {...props} />
      )}
    </>
  );
};

export type TSortableToggleItem = {
  label: string;
  value: string;
  enabled: boolean;
};

type AdminFormFieldDraggableToggleListProps = {
  loadingData?: boolean;
  label: string | false;
  id: string;
  items: TSortableToggleItem[];
  isDraggable?: boolean;
  onChange?: (items: TSortableToggleItem[]) => void;
  previewRenderer?: (currentValue: TSortableToggleItem[]) => ReactNode;
  onBlur?: () => void;
  touched?: boolean;
  error?: string;
  disabled?: boolean;
  detailsFormField?: boolean;
  customWrapperClass?: string;
};

export const AdminFormFieldDraggableToggleList: FC<
  AdminFormFieldDraggableToggleListProps
> = ({
  loadingData = false,
  label,
  id,
  items,
  isDraggable = true,
  onChange,
  previewRenderer,
  onBlur,
  touched,
  error,
  disabled = false,
  detailsFormField = false,
  customWrapperClass = "col-span-6",
}) => {
    const fieldHasError = detailsFormField
      ? !disabled && !!error
      : touched && !!error;

    const labelClass = fieldHasError
      ? "text-field-error-dark"
      : "text-field-label-valid";

    const previewOutput = previewRenderer?.(items);

    const onDragEnd = (result: DropResult) => {
      if (!result.destination || disabled || !isDraggable) {
        return;
      }

      const reorderedItems = Array.from(items);
      const [movedItem] = reorderedItems.splice(result.source.index, 1);
      reorderedItems.splice(result.destination.index, 0, movedItem);

      onChange?.(reorderedItems);
      onBlur?.();
    };

    const handleToggle = (targetValue: string) => {
      if (disabled) {
        return;
      }

      const updatedItems = items.map((item) =>
        item.value === targetValue ? { ...item, enabled: !item.enabled } : item
      );
      onChange?.(updatedItems);
      onBlur?.();
    };

    if (loadingData) {
      return (
        <PseudoSelect label={label} customWrapperClass={customWrapperClass} />
      );
    }

    return (
      <div className={customWrapperClass}>
        {!!label && (
          <label
            htmlFor={id}
            className={`block text-sm font-display font-semibold ${labelClass}`}
          >
            {label}
          </label>
        )}

        {isDraggable ? (
          <DragDropContext
            onDragEnd={onDragEnd}
            dragHandleUsageInstructions="Use handle to reorder"
          >
            <Droppable droppableId={`${id}__droppable`}>
              {(provided) => (
                <div
                  className={`mt-1 ${disabled ? "bg-gray-100" : "bg-white"} border ${fieldHasError ? "border-field-error-border" : "border-gray-200"} rounded-lg p-2 space-y-2`}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {items.map((item, index) => (
                    <Draggable
                      key={`${id}__${item.value}`}
                      draggableId={`${id}__${item.value}`}
                      index={index}
                      isDragDisabled={disabled || !item.enabled}
                    >
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className={`flex items-center justify-between rounded-lg border px-3 py-2 ${snapshot.isDragging ? "border-black/40 bg-accent-blue-pale" : "border-gray-200"} ${disabled ? "opacity-70" : "bg-white"}`}
                        >
                          <label
                            htmlFor={`${id}__${item.value}`}
                            className={`flex items-center gap-3 ${disabled ? "cursor-default" : "cursor-pointer"}`}
                          >
                            <input
                              id={`${id}__${item.value}`}
                              type="checkbox"
                              checked={item.enabled}
                              onChange={() => handleToggle(item.value)}
                              disabled={disabled}
                              className={`size-4 rounded border-gray-300${disabled ? " opacity-25" : ""}`}
                            />
                            <span className="text-sm text-gray-900">
                              {item.label}
                            </span>
                          </label>

                          <div
                            className={`group flex items-center justify-center size-8 rounded ${disabled || !item.enabled ? "cursor-default opacity-50 pointer-events-none touch-none" : "cursor-move bg-black/5 hover:bg-accent-blue-dark active:bg-accent-blue-dark"}`}
                            aria-label={`Drag ${item.label}`}
                            {...dragProvided.dragHandleProps}
                          >
                            <img
                              src={svgIcons.DragHandleBars}
                              alt="drag-handle"
                              className="size-5 group-hover:brightness-0 group-hover:invert group-active:brightness-0 group-active:invert"
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div
            className={`mt-1 ${disabled ? "bg-gray-100" : "bg-white"} border ${fieldHasError ? "border-field-error-border" : "border-gray-200"} rounded-lg p-2 space-y-2`}
          >
            {items.map((item) => (
              <div
                key={`${id}__${item.value}`}
                className={`flex items-center justify-between rounded-lg border px-3 py-2 border-gray-200 ${disabled ? "opacity-70" : "bg-white"}`}
              >
                <label
                  htmlFor={`${id}__${item.value}`}
                  className={`flex items-center gap-3 ${disabled ? "cursor-default" : "cursor-pointer"}`}
                >
                  <input
                    id={`${id}__${item.value}`}
                    type="checkbox"
                    checked={item.enabled}
                    onChange={() => handleToggle(item.value)}
                    disabled={disabled}
                    className={`size-4 rounded border-gray-300${disabled ? " opacity-25" : ""}`}
                  />
                  <span className="text-sm text-gray-900">{item.label}</span>
                </label>
              </div>
            ))}
          </div>
        )}

        {previewOutput}

        {fieldHasError && (
          <p className="bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1">
            {error}
          </p>
        )}
      </div>
    );
  };

export const AdminFormFieldAsyncDropdown: FC<
  AdminFormFieldAsyncDropdownProps
> = ({
  label,
  id,
  loadingData,
  name,
  placeholder = "",
  onChange,
  onBlur,
  value,
  defaultOptions = [],
  noOptionsMessage = () => "No options found",
  defaultOption = null,
  loadOptions,
  touched,
  error,
  disabled = false,
  readOnly = false,
  detailsFormField = false,
  customSelectboxClass = "",
  customWrapperClass = "col-span-6",
  isClearable,
  isMulti = false,
  HideDropdownIndicator,

  labelSubtext,
  bottomLabel,

  menuPlacement = "auto",
  maxMenuHeight = undefined,
  searchStyle = false,
  searchStyleOnClear = () => {},
}) => {
    const fieldHasError = detailsFormField
      ? !disabled && !!error
      : touched && !!error;
    let wrapperClass = fieldHasError ? "" : "";
    let labelClass = fieldHasError
      ? "text-field-error-dark"
      : "text-field-label-valid";
    let inputClass = fieldHasError
      ? "border-field-error-border focus-visible:outline-field-error-dark text-field-error-dark"
      : "border-gray-200 text-field-label-valid";
    const floatingError = false;
    const { colors } = useThemeAssets();

    const [selected, setSelected] = useState<TSelectboxOption[] | string | null>(
      value ?? ""
    );
    const handleChange = (e: TSelectboxOption | null) => {
      setSelected(`${e?.value}`);
      onChange?.(e);
    };

    return (
      <div
      className={`${
        floatingError ? ` relative pb-4` : ""
          } ${wrapperClass} ${customWrapperClass}`}
      >
        {!!label && (
          <label
            htmlFor={id}
            className={`block text-sm font-display font-semibold ${labelClass}`}
          >
            {label}
            {labelSubtext && (
              <span className="inline-block text-[10px] text-gray-400 ms-2">
                {labelSubtext}
              </span>
            )}
          </label>
        )}

        <AsyncSelect
          components={{
            ...(typeof HideDropdownIndicator === "boolean" && {
              DropdownIndicator: HideDropdownIndicator
                ? () => null
                : components.DropdownIndicator,
            }),
          }}
          inputId={id}
          isMulti={isMulti as false} // a typescript def issue ( https://github.com/JedWatson/react-select/issues/5522 )
          name={name}
          isLoading={loadingData}
          classNames={{
            control: (controlProps) => {
              // console.log('classNames state', controlProps)
            return `${!!label ? "mt-1" : ""} w-full min-h-11 px-1 ${searchStyle || true ? " pl-7" : ""} rounded-md bg-white text-sm shadow-sm border-2 focus-visible:outline-4 focus-visible:shadow-none ${inputClass} ${customSelectboxClass} ${
              controlProps.isFocused ? "border-red-600" : "border-grey-300"
                } ${controlProps.isDisabled ? "bg-gray-400" : ""}`;
            },
          }}
          styles={{
            control: (styles, controlProps) => {
              // console.log('styles state', styles, controlProps)
              return {
                ...styles,
                "&:before": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "10px",
                  transform: "translateY(-50%)",
                  width: "16px",
                  height: "16px",
                  background: `url(${svgIcons.SearchIcon}) no-repeat center`,
                  backgroundSize: "contain",
                  marginLeft: "4px",
                  // zIndex: 1, // disabled zIndex as this is not needed anymove after moving :before to control styles
                },
                minHeight: "44px",
                border:
                  !!error && !controlProps.isDisabled
                    ? `2px solid #D0021B`
                    : controlProps.isFocused
                      ? colors.dark
                      : "1px solid #e5e7eb",
                borderRadius: "6px",
                boxShadow: "none",
                outline: "none",
                "&:hover": {
                  borderColor:
                    !!error && !controlProps.isDisabled
                      ? "#D0021B"
                      : controlProps.isFocused
                        ? "#005FCC"
                        : "#e5e7eb",
                },
              };
            },
            container: (styles, containerProps) => {
              return {
                ...styles,
                // initially added to increase zIndex when focus and prevent :before search icon to overlap over active dropdown in cascaded dropdowns
                // however as :before is moved above to control styles, this is not needed anymore
                // zIndex: containerProps.isFocused ? 10 : "auto",
              };
            },
            valueContainer: (styles, valueContainerProps) => {
              let customStyles: CSSObjectWithLabel = {};
              if (searchStyle) {
                customStyles = {
                  // overflow: searchStyle ? 'visible' : 'hidden', // disabled as this makes the input overflow the container as you type a long text
                  // '&:before': { // moved to container
                  //   content: '""',
                  //   position: 'absolute',
                  //   top: '50%',
                  //   left: '10px',
                  //   transform: 'translateY(-50%)',
                  //   width: '16px',
                  //   height: '16px',
                  //   background: `url(${svgIcons.SearchIcon}) no-repeat center`,
                  //   backgroundSize: 'contain',
                  //   marginLeft: '-24px',
                  // }
                };
              }
              return {
                ...styles,
                ...customStyles,
              };
            },
            option: (styles, { isSelected, isFocused }) => {
              return {
                ...styles,
                ...(colors
                  ? {
                    backgroundColor: isSelected
                      ? colors.dark
                      : isFocused
                        ? colors.fade
                        : "",
                  }
                  : {}),
              };
            },
          }}
          placeholder={placeholder}
          cacheOptions
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          value={defaultOptions.find((optItem) => optItem.value === selected)}
          onChange={(e: any) => handleChange(e)}
          isDisabled={disabled || readOnly}
          menuPlacement={menuPlacement}
          maxMenuHeight={maxMenuHeight}
          defaultValue={defaultOption}
          noOptionsMessage={noOptionsMessage}
          {...(typeof isClearable === "boolean" && { isClearable })}
          {...(searchStyle && {
            components: {
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
              ClearIndicator: (props) => {
                // reference: https://codesandbox.io/p/sandbox/react-select-custom-clearindicator-c8fxv?file=%2Fexample.js%3A18%2C48-18%2C71
                // reference: https://codesandbox.io/p/sandbox/react-select-custom-valuecontainer-nnvmu?file=%2Findex.js%3A14%2C24-35%2C2
                const clearValue = () => {
                  props.clearValue();
                  // props.selectProps?.onClear && props.selectProps.onClear();
                  searchStyleOnClear?.();
                };

                const innerProps = {
                  ...props.innerProps,
                  onMouseDown: clearValue,
                  onTouchEnd: clearValue,
                };
                return (
                  <components.ClearIndicator {...props} innerProps={innerProps}>
                    <span className="block cursor-pointer">
                      <img
                        src={svgIcons.CloseIcon}
                        alt=""
                        className="size-5 opacity-50"
                      />
                    </span>
                  </components.ClearIndicator>
                );
              },
            },
            isClearable: true,
          })}
        />
        {bottomLabel && (
          <span className="block text-[10px] text-gray-400 mt-1">
            {bottomLabel}
          </span>
        )}
        {fieldHasError && (
          <p
          className={`${
            floatingError ? "absolute" : ""
              } bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
          >
            {error}
          </p>
        )}
      </div>
    );
  };

export const AdminFormFieldAsyncLazyDropdown: FC<
  AdminFormFieldAsyncLazyDropdownProps
> = ({
  label,
  id,
  loadingData,
  name,
  placeholder = "",
  onChange,
  onBlur,
  value,
  defaultOptions = [],
  noOptionsMessage = () => "No options found",
  defaultOption = null,
  additional = { page: 1 },
  loadOptions,
  touched,
  error,
  disabled = false,
  readOnly = false,
  detailsFormField = false,
  customSelectboxClass = "",
  customWrapperClass = "col-span-6",
  isClearable,
  isMulti = false,
  isSearchable = true,
  HideDropdownIndicator,
  labelSubtext,
  bottomLabel,

  menuPlacement = "auto",
  maxMenuHeight = undefined,
  searchStyle = false,
  searchStyleOnClear = () => {},
}) => {
    const fieldHasError = detailsFormField
      ? !disabled && !!error
      : touched && !!error;
    let wrapperClass = fieldHasError ? "" : "";
    let labelClass = fieldHasError
      ? "text-field-error-dark"
      : "text-field-label-valid";
    let inputClass = fieldHasError
      ? "border-field-error-border focus-visible:outline-field-error-dark text-field-error-dark"
      : "border-gray-200 text-field-label-valid";
    const floatingError = false;

    const { colors } = useThemeAssets();
    const handleChange = (e: TSelectboxOption | null) => {
      onChange?.(e);
    };
    const selected = useMemo(() => {
      if (Array.isArray(value)) {
        return value;
      }

      if (typeof value === "object" && value !== null) {
        return value;
      }

      if (!!isMulti) {
        return (
          defaultOptions?.filter((optItem) => optItem.value === value) ?? null
        );
      }

      return defaultOptions?.find((optItem) => optItem.value === value);
    }, [defaultOptions, isMulti, value]);
    return (
      <div
      className={`${
        floatingError ? ` relative pb-4` : ""
          } ${wrapperClass} ${customWrapperClass}`}
      >
        {!!label && (
          <label
            htmlFor={id}
            className={`block text-sm font-display font-semibold ${labelClass}`}
          >
            {label}
            {labelSubtext && (
              <span className="inline-block text-[10px] text-gray-400 ms-2">
                {labelSubtext}
              </span>
            )}
          </label>
        )}

        <AsyncPaginate
          components={{
            ...(typeof HideDropdownIndicator === "boolean" && {
              DropdownIndicator: HideDropdownIndicator
                ? () => null
                : components.DropdownIndicator,
            }),
          }}
          // rendoptionRenderer={renderOption}
          inputId={id}
          isSearchable={isSearchable}
          isMulti={isMulti as false} // a typescript def issue ( https://github.com/JedWatson/react-select/issues/5522 )
          closeMenuOnSelect={!isMulti} // false - close menu only if multi select is active
          name={name}
          isLoading={loadingData}
          classNames={{
            control: (controlProps: any) => {
              // console.log('classNames state', controlProps)
            return `${!!label ? "mt-1" : ""} w-full min-h-11 px-1 ${(searchStyle || true) && !!isSearchable ? " pl-7" : ""} rounded-md bg-white text-sm shadow-sm border-2 focus-visible:outline-4 focus-visible:shadow-none ${inputClass} ${customSelectboxClass} ${
              controlProps.isFocused ? "border-red-600" : "border-grey-300"
                } ${controlProps.isDisabled ? "bg-gray-400" : ""}`;
            },
          }}
          styles={{
            control: (styles: any, controlProps: any) => {
              // console.log('styles state', styles, controlProps)
              return {
                ...styles,
                ...(isSearchable
                  ? {
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: "10px",
                      transform: "translateY(-50%)",
                      width: "16px",
                      height: "16px",
                      background: `url(${svgIcons.SearchIcon}) no-repeat center`,
                      backgroundSize: "contain",
                      marginLeft: "4px",
                      // zIndex: 1, // disabled zIndex as this is not needed anymove after moving :before to control styles
                    },
                  }
                  : {}),
                minHeight: "44px",
                border:
                  !!error && !controlProps.isDisabled
                    ? `2px solid #D0021B`
                    : controlProps.isFocused
                      ? colors.dark
                      : "1px solid #e5e7eb",
                borderRadius: "6px",
                boxShadow: "none",
                outline: "none",
                "&:hover": {
                  borderColor:
                    !!error && !controlProps.isDisabled
                      ? "#D0021B"
                      : controlProps.isFocused
                        ? "#005FCC"
                        : "#e5e7eb",
                },
              };
            },
            container: (styles: any, containerProps: any) => {
              return {
                ...styles,
                // initially added to increase zIndex when focus and prevent :before search icon to overlap over active dropdown in cascaded dropdowns
                // however as :before is moved above to control styles, this is not needed anymore
                // zIndex: containerProps.isFocused ? 10 : "auto",
              };
            },
            valueContainer: (styles: any, valueContainerProps: any) => {
              let customStyles: CSSObjectWithLabel = {};
              if (searchStyle) {
                customStyles = {
                  // overflow: searchStyle ? 'visible' : 'hidden', // disabled as this makes the input overflow the container as you type a long text
                  // '&:before': { // moved to container
                  //   content: '""',
                  //   position: 'absolute',
                  //   top: '50%',
                  //   left: '10px',
                  //   transform: 'translateY(-50%)',
                  //   width: '16px',
                  //   height: '16px',
                  //   background: `url(${svgIcons.SearchIcon}) no-repeat center`,
                  //   backgroundSize: 'contain',
                  //   marginLeft: '-24px',
                  // }
                };
              }
              return {
                ...styles,
                ...customStyles,
              };
            },
            option: (styles, { isSelected, isFocused }) => {
              return {
                ...styles,
                ...(colors
                  ? {
                    backgroundColor: isSelected
                      ? colors.dark
                      : isFocused
                        ? colors.fade
                        : "",
                  }
                  : {}),
              };
            },
            placeholder: (styles, { isDisabled, isFocused }) => {
              return {
                ...styles,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              };
            },
          }}
          placeholder={placeholder}
          defaultOptions={defaultOptions}
          additional={additional}
          loadOptions={loadOptions}
          //value={defaultOptions.find((optItem) => optItem.value === selected)}
          value={selected}
          onChange={(e: any) => handleChange(e)}
          isDisabled={disabled || readOnly}
          menuPlacement={menuPlacement}
          maxMenuHeight={maxMenuHeight}
          defaultValue={defaultOption}
          noOptionsMessage={noOptionsMessage}
          {...(typeof isClearable === "boolean" && { isClearable })}
          {...(searchStyle && {
            components: {
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
              ClearIndicator: (props: any) => {
                // reference: https://codesandbox.io/p/sandbox/react-select-custom-clearindicator-c8fxv?file=%2Fexample.js%3A18%2C48-18%2C71
                // reference: https://codesandbox.io/p/sandbox/react-select-custom-valuecontainer-nnvmu?file=%2Findex.js%3A14%2C24-35%2C2
                const clearValue = () => {
                  props.clearValue();
                  // props.selectProps?.onClear && props.selectProps.onClear();
                  searchStyleOnClear?.();
                };

                const innerProps = {
                  ...props.innerProps,
                  onMouseDown: clearValue,
                  onTouchEnd: clearValue,
                };
                return (
                  <components.ClearIndicator {...props} innerProps={innerProps}>
                    <span className="block cursor-pointer">
                      <img
                        src={svgIcons.CloseIcon}
                        alt=""
                        className="size-5 opacity-50"
                      />
                    </span>
                  </components.ClearIndicator>
                );
              },
            },
            isClearable: true,
          })}
        />
        {bottomLabel && (
          <span className="block text-[10px] text-gray-400 mt-1">
            {bottomLabel}
          </span>
        )}
        {fieldHasError && (
          <p
          className={`${
            floatingError ? "absolute" : ""
              } bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
          >
            {error}
          </p>
        )}
      </div>
    );
  };

type AdminFormFieldChecboxProps = Omit<
  AdminFormFieldInputProps,
  "value" | "label"
> & {
  label: string | ReactNode;
  title?: string;
  description?: string | null;
  checked?: boolean;
  icon?: string | null;
  IconComponent?: ReactNode;
  wrapperPaddingClass?: string;
  isPrimaryListItem?: boolean;
  fullComponentClickable?: boolean;
};

export const AdminFormFieldCheckbox: FC<AdminFormFieldChecboxProps> = ({
  label,
  title,
  id,
  type,
  name,
  checked = false,
  onChange,
  onBlur,
  touched,
  error,
  disabled = false,
  icon = null,
  IconComponent = null,
  customWrapperClass = "col-span-4",
  wrapperPaddingClass = "p-3",
  isPrimaryListItem = false,
  fullComponentClickable = true,
  readOnly = false,
  description = null,
}) => {
  const inputRef = React.createRef<HTMLInputElement>();
  const handleChange = (e: any) => {
    onChange?.(e);
  };

  const FieldWrapperElement = useCallback(
    ({ children, ...restProps }: any) => {
      if (fullComponentClickable) {
        return (
          <label htmlFor={id} {...restProps}>
            {children}
          </label>
        );
      } else {
        return <div {...restProps}>{children}</div>;
      }
    },
    [id, fullComponentClickable]
  );

  return (
    <FieldWrapperElement
      className={`bg-white flex justify-between items-center ${wrapperPaddingClass} rounded-lg shadow-md shadow-black/5 border border-black/10${disabled ? " opacity-50" : ""} ${disabled ? "cursor-default" : "cursor-pointer"} ${customWrapperClass}`}
      title={title}
      onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        if (!fullComponentClickable) {
          if (disabled || readOnly) return;
          if (
            (e.target as HTMLInputElement).classList.contains(
              "checkbox-wrapper"
            )
          ) {
            if (inputRef?.current)
              inputRef.current.checked = !inputRef?.current?.checked;
            handleChange({
              target: {
                checked: !checked,
                value: !checked ? true : false,
                name: name,
              },
            });
          }
        }
      }}
    >
      <div className="flex flex-col gap-1">
        <span className="flex gap-2 items-center">
          {icon && <img src={icon} alt="icon" />}
          {IconComponent}
          <span
            className={`block font-display font-semibold ${isPrimaryListItem ? "text-accent-blue-dark text-base" : "text-sm text-field-label-valid"}`}
          >
            {label}
          </span>
        </span>
        {description && (
          <span className="text-xs text-gray-700">
            {description}
          </span>
        )}
      </div>
      <div className="checkbox-wrapper">
        <input
          ref={inputRef}
          type={type}
          id={id}
          name={name}
          checked={checked}
          onChange={handleChange}
          onBlur={onBlur}
          className="mt-1 w-12 h-11 px-3 disabled:bg-gray-200 disabled:border-gray-300 rounded-md text-sm shadow-sm sr-only peer focus-visible:outline-4 focus-visible:shadow-none"
          disabled={disabled}
          readOnly={readOnly}
        />
        <div
          onClick={() =>
            disabled || readOnly
              ? () => {}
              : handleChange({
                target: {
                  checked: !checked,
                  value: !checked ? true : false,
                  name: name,
                },
              })
          }
          className="relative w-12 h-7 pointer-events-none touch-none bg-gray-200 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:start-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500 peer-focus:outline outline-2 outline-accent-blue-dark"
        ></div>
      </div>
    </FieldWrapperElement>
  );
};

interface RadioCheckboxProps {
  label: string | ReactNode;
  radioChecked: boolean;
  onRadioChange: () => void;
  checked: boolean;
  onCheckboxChange: (checked: boolean) => void;
  disabled?: boolean;
  isRadioButton?: boolean;
}

export const AdminFormFieldRadioCheckbox: FC<RadioCheckboxProps> = ({
  label,
  radioChecked,
  onRadioChange,
  checked,
  onCheckboxChange,
  disabled = false,
  isRadioButton= false
}) => {
  return (
<div
  className={`flex items-center justify-between p-3 bg-white rounded-lg shadow-md shadow-black/5 border ${
    radioChecked
      ? "border-accent-blue-dark"
      : "border-black/10"
  } ${disabled ? "opacity-50" : ""}`}
>

      <label className="flex items-center gap-2 min-w-0 cursor-pointer">
        {isRadioButton &&
        <input
          type="radio"
          checked={radioChecked}
          onChange={onRadioChange}
          disabled={disabled}
          className="h-4 w-4 shrink-0 rounded-full appearance-auto accent-blue-600"
        />}

        <span className="text-sm font-semibold break-words">
          {label}
        </span>
      </label>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCheckboxChange?.(!checked);
        }}
        disabled={disabled}
        className={`relative w-12 h-7 shrink-0 rounded-full transition-colors ${checked ? "bg-green-500" : "bg-gray-200"
          }`}
      >
        <span
          className={`absolute top-1 left-1 h-5 w-5 rounded-full bg-white border border-gray-300 transition-transform ${checked ? "translate-x-5" : ""
            }`}
        />
      </button>
    </div>
  );
};

export const RadioButtonConfigurationOption = ({
  value,
  title,
  description,
  points,
  selected,
  onChange,
}: {
  value: string;
  title: string;
  description: string;
  points: string[];
  selected: boolean;
  onChange: (value: string) => void;
}) => {
  const handleChange = (e: any) => {
    onChange?.(e);
  };
  return (
    <label
      className={`block h-full cursor-pointer rounded border p-3 ${selected ? "border-blue-600" : "border-gray-300"
        }`}
    >
      <div className="flex gap-3">
        <input
          type="radio"
          name="configuration"
          value={value}
          checked={selected}
          onChange={() => handleChange(value)}
          className="sr-only"
        />

        <span
          aria-hidden="true"
          className={`relative h-5 w-5 shrink-0 rounded-full border-2 ${selected ? "border-blue-600" : "border-gray-400"
            }`}
        >
          {selected && (
            <span className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600" />
          )}
        </span>

        <div>
          <h3 className="font-display text-sm font-semibold text-gray-900">
            {title}
          </h3>

          <p className="mt-1 text-xs leading-5 text-gray-600">
            {description}
          </p>

          <ul className="mt-3 list-disc space-y-1 pl-5 text-xs leading-5 text-gray-700">
            {points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      </div>
    </label>
  );
};

const LabelAsideCheckbox: FC<{ label?: string; description?: string }> = ({
  label,
  description,
}) => (
  <span
    className={`flex flex-col items-center justify-center text-center pointer-events-none touch-none`}
  >
    <span
      className={`${!!description ? "leading-4" : "leading-8"} text-xs font-bold uppercase text-accent-blue-dark`}
    >
      {label}
    </span>
    {!!description && (
      <span className="leading-3 text-[10px] text-accent-blue-dark opacity-70">
        {description}
      </span>
    )}
  </span>
);

export const AdminFormFieldCheckboxWithOnOffLabels: FC<
  AdminFormFieldChecboxProps & {
    label?: string;
    offLabel: string;
    offLabelDescription?: string;
    onLabel: string;
    onLabelDescription?: string;
  }
> = ({
  label,
  offLabel,
  offLabelDescription,
  onLabel,
  onLabelDescription,
  id,
  type,
  name,
  checked = false,
  onChange,
  onBlur,
  touched,
  error,
  disabled = false,
  icon = null,
  IconComponent = null,
  customWrapperClass = "col-span-6",
}) => {
    const handleChange = (e: any) => {
      onChange?.(e);
    };

    const widthClass = `w-16`; // `w-32`;
    const peerCheckedClass = `peer-checked:after:translate-x-8 rtl:peer-checked:after:-translate-x-full`; // `peer-checked:after:translate-x-24 rtl:peer-checked:after:-translate-x-full`;

    return (
      <div className={`${customWrapperClass} flex flex-row justify-between`}>
        <span></span>
        <label
          htmlFor={id}
          className={`bg-white flex justify-between items-center ${disabled ? "opacity-50 pointer-events-none touch-none cursor-default" : "cursor-pointer"}`}
        >
          <span className="flex gap-2 items-center">
            {icon && <img src={icon} alt="icon" />}
            {IconComponent}
            <span
              className={`block text-sm font-display font-semibold text-field-label-valid`}
            >
              {label}
            </span>
          </span>
          <div className="flex flex-row gap-2">
            <LabelAsideCheckbox
              label={offLabel}
              description={offLabelDescription}
            />
            <div className="relative z-0">
              <input
                type={type}
                id={id}
                name={name}
                checked={checked}
                onChange={handleChange}
                onBlur={onBlur}
                className={`mt-1 ${widthClass} h-14 px-3 disabled:bg-gray-200 disabled:border-gray-300 rounded-md text-sm shadow-sm sr-only peer focus-visible:outline-4 focus-visible:shadow-none`}
                disabled={disabled}
              />
              <div
                onClick={() =>
                  disabled
                  ? () => {}
                    : handleChange({
                      target: {
                        checked: !checked,
                        value: !checked ? true : false,
                        name: name,
                      },
                    })
                }
                className={
                  `relative ${widthClass} h-8 pointer-events-none touch-none bg-white border-2 border-accent-blue-pale rounded-full ` +
                  `after:ml-0.5 ${peerCheckedClass} peer-checked:after:border-white peer-checked:after:ml-0 ` +
                  `after:content-[''] after:absolute after:top-1.5 after:-mt-[2px] after:start-1 after:bg-accent-blue-pale after:border-gray-300 after:border ` +
                  `after:rounded-full after:h-5 after:w-5 after:transition-all Xpeer-checked:bg-green-500 peer-focus:border-accent-blue-dark`
                }
              ></div>
            </div>
            <LabelAsideCheckbox
              label={onLabel}
              description={onLabelDescription}
            />
          </div>
        </label>
      </div>
    );
  };

export type AdminFormFieldSubmitProps = {
  type?: "submit" | "button";
  variant?:
  | "primary"
  | "primary-like"
  | "primary-alt"
  | "secondary"
  | "danger-transparent"
  | "danger"
  | "success"
  | "success-like"
  | "danger-alt"
  | "text-like";
  label: string | ReactNode;
  id?: string;
  titleText?: string;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  customWrapperClass?: string;
  buttonDimensionClass?: string;
  buttonTextClass?: string;
};

export const AdminFormFieldSubmit: FC<AdminFormFieldSubmitProps> = ({
  type = "submit",
  variant = "secondary",
  label,
  id,
  titleText = "",
  onClick,
  disabled = false,
  customWrapperClass = "col-span-6",
  buttonDimensionClass = "h-10 w-full px-4",
  buttonTextClass = "text-sm font-display font-semibold",
}) => {
  let variantClass = "";
  const disabledClass = "border-gray-200 bg-gray-200 text-gray-400";
  switch (variant) {
    case "success":
      variantClass = disabled
        ? disabledClass
        : "border-0 bg-accent-green text-white enabled:hover:opacity-80 ring-green-200";
      break;
    case "success-like":
      variantClass = disabled
        ? disabledClass
        : "border-0 bg-accent-customGreen text-white enabled:hover:opacity-80 ring-transparent";
      break;
    case "primary":
      variantClass = disabled
        ? disabledClass
        : "border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark enabled:hover:bg-accent-blue-dark enabled:hover:text-white enabled:focus:bg-accent-blue-dark enabled:focus:text-white active:bg-accent-blue-pale enabled:focus:outline enabled:focus:-outline-offset-1 enabled:focus:outline-accent-blue-dark ring-accent-blue-pale";
      break;
    case "primary-like": // opacity on hover
      variantClass = disabled
        ? disabledClass
        : "border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark enabled:hover:opacity-80 active:bg-accent-blue-pale enabled:focus:outline enabled:focus:-outline-offset-1 enabled:focus:outline-accent-blue-dark ring-accent-blue-pale";
      break;
    case "primary-alt": // used as button on accordion handle
      variantClass = disabled
        ? disabledClass
        : "border-accent-blue-dark/40 bg-white/40 text-accent-blue-dark enabled:hover:bg-accent-blue-dark/10 active:bg-accent-blue-pale enabled:focus:outline enabled:focus:-outline-offset-1 enabled:focus:outline-accent-blue-dark ring-accent-blue-pale";
      break;
    case "danger-transparent":
      variantClass = disabled
        ? disabledClass
        : "border-0 bg-transparent text-field-error-dark enabled:hover:bg-field-error-dark/10 ring-field-error-dark/20";
      break;
    case "danger":
      variantClass = disabled
        ? disabledClass
        : "border border-red-600 bg-transparent text-field-error-dark enabled:hover:bg-field-error-dark/10 ring-field-error-dark/20";
      break;
    case "danger-alt":
      variantClass = disabled
        ? disabledClass
        : " border-red-600 bg-transparent text-field-error-dark enabled:focus:outline enabled:focus:-outline-offset-1";
      break;
    case "text-like":
      variantClass = disabled
        ? " border-0 text-gray-400"
        : " border-0 bg-transparent enabled:hover:bg-accent-blue-paleO66 text-accent-blue-dark enabled:focus:outline enabled:focus:-outline-offset-1";
      break;
    case "secondary":
    default:
      variantClass = disabled
        ? disabledClass
        : (variantClass =
          "border-accent-blue-dark bg-transparent text-accent-blue-dark enabled:hover:bg-accent-blue-pale active:bg-accent-blue-dark active:text-accent-blue-pale");
  }
  const Button = () => (
    <button
      className={`flex items-center justify-center group ${buttonDimensionClass} rounded-lg border ${buttonTextClass} transition ${variantClass.includes("focus:outline") ? "" : "focus:outline-none"} focus:ring ${variantClass}`}
      type={type}
      id={id}
      onClick={onClick}
      disabled={disabled}
      title={titleText}
    >
      {label}
    </button>
  );
  return (
    <>
      {!!customWrapperClass ? (
        <div className={customWrapperClass}>
          <Button />
        </div>
      ) : (
        <Button />
      )}
    </>
  );
};

type AdminFormFieldPillListProps = {
  customWrapperClass?: string;
  disabled?: boolean;
  children?: ReactNode;
  heightClass?: string;
  isError?: boolean;
  paddingClass?: string;
};

export const AdminFormFieldPillList: FC<AdminFormFieldPillListProps> = ({
  customWrapperClass = "col-span-12",
  disabled = false,
  children,
  heightClass = "min-h-24",
  isError = false,
  paddingClass = "p-3",
}) => {
  return (
    <div
      className={`${customWrapperClass} ${paddingClass} gap-2 ${!disabled ? "bg-white" : "bg-gray-100"} border ${isError ? "border-field-error-border focus-visible:outline-field-error-dark" : "border-gray-300"}  rounded-lg flex items-start flex-wrap ${heightClass}`}
    >
      {children}
    </div>
  );
};

export type PillItem = {
  name: string;
};
type AdminFormFieldPillItemProps = {
  disabled: boolean;
  item: PillItem;
  onItemClick?: (item: PillItem) => void;
  onRemove: (item: any) => void;
};

export const AdminFormFieldPillItem: FC<AdminFormFieldPillItemProps> = ({
  disabled,
  item,
  onItemClick,
  onRemove,
}) => {
  return (
    <div
      className={`flex items-center bg-gray-200 rounded-lg py-1 relative ${disabled ? "px-2" : "ps-2 pe-9 hover:outline"} outline-black/20 cursor-default`}
    >
      <button
        type="button"
        className={`p-1 font-semibold text-sm text-gray-500 leading-4 tracking-tighter${!!onItemClick ? "" : " cursor-default"}`}
        onClick={!!onItemClick ? () => onItemClick?.(item) : undefined}
        disabled={disabled}
      >
        {item.name}
      </button>
      {!disabled && (
        <button
          className="absolute group top-0 right-0 bg-black/10 hover:bg-black/50 focus:bg-black/50 p-[6px] rounded-tr-lg rounded-br-lg cursor-pointer ring-0 outline-0"
          type="button"
          onClick={() => onRemove(item)}
        >
          <img
            src={svgIcons.CloseIcon}
            alt={item.name}
            className="size-5 cursor-pointer group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
          />
        </button>
      )}
    </div>
  );
};

type AdminFormFieldDropdownButtonProps = {
  label: string;
  wrapperSpacingClass?: string;
  wrapperClass?: string;
  showDropdownIcon?: boolean;
  setShowDropdownIcon?: React.Dispatch<React.SetStateAction<boolean>>;
  forcedIcon?: string | null;
};

export const AdminFormFieldDropdownButton: FC<
  AdminFormFieldDropdownButtonProps
> = ({
  label,
  wrapperSpacingClass = "pl-4 pr-2",
  wrapperClass = "border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark hover:bg-accent-blue-dark hover:text-white",
  showDropdownIcon = false,
  setShowDropdownIcon,
  forcedIcon = null,
}) => {
    return (
      <button
        className={`group gap-1 relative flex items-center justify-center h-10 rounded-lg border text-sm font-display font-semibold transition focus:outline-none focus:ring cursor-pointer ${wrapperClass} ${wrapperSpacingClass}`}
        onClick={() => {
          setShowDropdownIcon && setShowDropdownIcon(!showDropdownIcon);
        }}
      >
        {label}
        <img
          alt="download"
          src={
            !!forcedIcon
              ? forcedIcon
              : showDropdownIcon
                ? svgIcons.CollapseIcon
                : svgIcons.ExpandMoreIcon
          }
          className="size-7 group-disabled:opacity-50 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
        />
      </button>
    );
  };

export const AdminFormFieldRadio: FC<
  AdminFormFieldInputProps & {
    customLabelClass?: string;
    customLabelTextClass?: string;
    checked?: boolean;
    customWrapperPositionClass?: string;
    title?: string;
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  }
> = ({
  label,
  title,
  subText,
  customLabelClass = "inline-flex items-center cursor-pointer",
  customLabelTextClass = "ml-2",
  type,
  id,
  name,
  placeholder = "",
  onClick,
  onChange,
  onBlur,
  value,
  checked = false,
  touched,
  error,
  disabled = false,
  readOnly = false,
  detailsFormField = false,
  customInputClass = "",
  customWrapperClass = "col-span-6",
  customWrapperPositionClass = "relative",
  disabledClass,
}) => {
    const fieldHasError = detailsFormField
      ? !disabled && !!error
      : touched && !!error;
    let wrapperClass = fieldHasError ? "" : "";
    const floatingError = false;
    return (
      <div
      className={`${customWrapperPositionClass} ${
        floatingError ? ` pb-4` : ""
          } ${wrapperClass} ${customWrapperClass}`}
      >
        <label htmlFor={id} className={customLabelClass}>
          <input
            type={type}
            id={id}
            name={name}
            value={value}
            checked={checked}
            placeholder={placeholder}
            onClick={onClick}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            readOnly={readOnly}
            className={`size-4 rounded-full text-primary focus:ring-primary border-gray-300 ${customInputClass} ${disabledClass ? disabledClass : `disabled:bg-gray-200 disabled:border-gray-300`}`}
          />
          <span className={customLabelTextClass} title={title}>
            {label}
            {subText && (
              <span className="block text-[10px] text-gray-400">{subText}</span>
            )}
          </span>
        </label>
        {fieldHasError && (
          <p
          className={`${
            floatingError ? "absolute" : ""
              } bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
          >
            {error}
          </p>
        )}
      </div>
    );
  };

type AdminFormFieldTextareaProps = {
  label: string;
  bottomLabel?: string;
  id: string;
  name: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  value?: string;
  touched?: boolean;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  height?: string; // New height prop
  customTextareaClass?: string;
  customWrapperClass?: string;
  detailsFormField?: boolean;
};

export const AdminFormFieldTextarea: FC<AdminFormFieldTextareaProps> = ({
  label,
  bottomLabel,
  id,
  name,
  placeholder = "",
  onChange,
  onBlur,
  value,
  touched,
  error,
  disabled = false,
  readOnly = false,
  height = "h-20", // Default height, can be overridden
  customTextareaClass = "",
  customWrapperClass = "col-span-6",
  detailsFormField = false,
}) => {
  const fieldHasError = detailsFormField
    ? !disabled && !!error
    : touched && !!error;
  let wrapperClass = fieldHasError ? "" : "";
  let labelClass = fieldHasError
    ? "text-field-error-dark"
    : "text-field-label-valid";
  let textareaClass = fieldHasError
    ? "border-field-error-border focus-visible:outline-field-error-dark text-field-error-dark"
    : "border-gray-200 text-field-label-valid";

  return (
    <div className={`relative ${wrapperClass} ${customWrapperClass}`}>
      <label
        htmlFor={id}
        className={`block text-sm font-display font-semibold ${labelClass}`}
      >
        {label}
      </label>

      <textarea
        id={id}
        name={name}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        className={`mt-1 w-full ${height} px-3 py-2 rounded-md text-sm shadow-sm enabled:outline-accent-blue-dark focus-visible:outline-4 focus-visible:shadow-none ${textareaClass} ${customTextareaClass} resize-none`} // Use height prop
      />

      {bottomLabel && (
        <span className="block text-[10px] text-gray-400 mt-1">
          {bottomLabel}
        </span>
      )}
      {fieldHasError && (
        <p
          className={`bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

type AdminFormFieldCheckboxProps = {
  label: string;
  value: string;
  icon?: string | null;
  IconComponent?: ReactNode;
};

type AdminFormFieldCheckboxGroupProps = {
  options: AdminFormFieldCheckboxProps[];
  layout?: "row" | "column";
  values?: string[];
  onChange: (values: string[]) => void; // This function takes an array of values
  disabled?: boolean;
  customWrapperClass?: string;
  selectionMode?: "single" | "multiple"; // New prop to determine selection mode
};

export const AdminFormFieldCheckboxGroup: FC<
  AdminFormFieldCheckboxGroupProps
> = ({
  options,
  layout = "column",
  values = [],
  onChange,
  disabled = false,
  customWrapperClass = "col-span-4",
  selectionMode = "multiple", // Default to multiple
}) => {
    const handleChange = (value: string) => {
      if (selectionMode === "single") {
        // For single selection, set the selected value as an array with one element
        onChange([value]);
      } else {
        // For multiple selection
        const newValues = values.includes(value)
          ? values.filter((v) => v !== value) // Remove value if it's already selected
          : [...values, value]; // Add value if it's not selected
        onChange(newValues); // Pass the new array of values back to the parent component
      }
    };

    return (
      <div
        className={`shadow-sm bg-white rounded-lg content-center ${customWrapperClass}`}
      >
        <div
          className={`flex justify-around ${layout === "row" ? "flex-row" : "flex-col"} px-3`}
        >
          {options.map(({ label, value, icon, IconComponent }, index) => (
            <label
              key={value}
              className={`flex items-center p-2 ${disabled ? "opacity-50" : ""} cursor-pointer`}
            >
              <input
                type="checkbox"
                checked={values.includes(value)}
                onChange={() => handleChange(value)} // Update selected values
                className="w-5 h-5 mr-3 text-blue-600 border-gray-300 rounded"
                style={{
                  borderColor: "#C2DCF2", // Set the checkbox color
                }}
                disabled={disabled}
              />

              <span className="flex gap-2 items-center">
                {icon && <img src={icon} alt="icon" className="h-5 w-5" />}
                {IconComponent}
                <span className="block text-sm font-display font-semibold text-field-label-valid">
                  {label}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  };

export const AdminFormFieldIntermediateCheckbox: FC<
  AdminFormFieldInputProps & {
    customLabelTextClass?: string;
    checked?: boolean;
    intermediate?: boolean;
  }
> = ({
  label,
  customLabelTextClass = "ml-2",
  type,
  id,
  name,
  placeholder = "",
  onChange,
  onBlur,
  value,
  checked = false,
  touched,
  error,
  disabled = false,
  readOnly = false,
  detailsFormField = false,
  customInputClass = "",
  customWrapperClass = "col-span-6",
  disabledClass,
  intermediate = false,
}) => {
    const fieldHasError = detailsFormField
      ? !disabled && !!error
      : touched && !!error;
    let wrapperClass = fieldHasError ? "" : "";
    const floatingError = false;
    return (
      <div
      className={`relative ${
        floatingError ? ` pb-4` : ""
          } ${wrapperClass} ${customWrapperClass}`}
        key={id}
        onChange={onChange}
      >
        <label htmlFor={id} className="inline-flex items-center cursor-pointer">
          <TriStateCheckbox
            id={id}
            checked={checked}
            intermediate={intermediate}
            value={value}
            disabled={disabled}
            onChange={onChange}
          />
          <span className={customLabelTextClass}>{label}</span>
        </label>
        {fieldHasError && (
          <p
          className={`${
            floatingError ? "absolute" : ""
              } bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
          >
            {error}
          </p>
        )}
      </div>
    );
  };

type AdminFormFieldMultiCheckboxOption = {
  label: string;
  fieldName: string;
};

type AdminFormFieldMultiCheckboxProps = {
  label: string;
  id: string;
  options: AdminFormFieldMultiCheckboxOption[];
  values: Record<string, boolean>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched?: Record<string, boolean>;
  error?: string;
  disabled?: boolean;
  detailsFormField?: boolean;
  customWrapperClass?: string;
};

export const AdminFormFieldMultiCheckbox: FC<
  AdminFormFieldMultiCheckboxProps
> = ({
  label,
  id,
  options,
  values = {},
  onChange,
  onBlur,
  touched,
  error,
  disabled = false,
  detailsFormField = false,
  customWrapperClass = "col-span-6",
}) => {
    const fieldHasError = detailsFormField
      ? !disabled && !!error
      : touched && !!error;

    let labelClass = fieldHasError
      ? "text-field-error-dark"
      : "text-field-label-valid";

    return (
      <div className={customWrapperClass}>
        <label
          className={`block text-sm font-display font-semibold mb-2 ${labelClass}`}
        >
          {label}
        </label>
        <div
          className={`${disabled ? "bg-gray-100" : "bg-white"} p-4 rounded-lg shadow-md shadow-black/5 border border-black/10 grid grid-cols-2 gap-3`}
        >
          {options.map((option) => (
            <label
              key={option.fieldName}
            className={`flex items-center p-2 rounded ${
              disabled
                  ? "opacity-50 cursor-default"
                  : "cursor-pointer hover:bg-gray-50"
                }`}
            >
              <input
                type="checkbox"
                id={option.fieldName}
                name={option.fieldName}
                checked={values[option.fieldName] || false}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                className="w-5 h-5 mr-3 text-blue-600 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="block text-sm font-display font-semibold text-field-label-valid">
                {option.label}
              </span>
            </label>
          ))}
        </div>
        {fieldHasError && (
          <p className="bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1">
            {error}
          </p>
        )}
      </div>
    );
  };
